import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { api } from 'api/api';
import TokenUtils from 'utils/TokenUtils';
import { addNotification } from './notificationsSlice';

export const getCashboxes = createAsyncThunk('cashboxes/getCashboxes', async (locationID, { rejectWithValue }) => {
  try {
    const response = await api.get('/cashboxes', {
      params: { locationID },
    });

    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Не вдалося отримати дані рахунків');
  }
});

export const getCashbox = createAsyncThunk('cashboxes/getCashbox', async (cashboxID, { rejectWithValue }) => {
  try {
    const response = await api.get(`/cashboxes/${cashboxID}`);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Не вдалося отримати дані рахунку');
  }
});

export const createCashbox = createAsyncThunk(
  'cashboxes/createCashbox',
  async (cashboxData, { dispatch, rejectWithValue }) => {
    try {
      const token = TokenUtils.getToken();
      const response = await api.post('/cashboxes', cashboxData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      dispatch(
        addNotification({
          id: Math.random().toString(36).substr(2, 9),
          title: 'Рахунок успішно створено',
          type: 'success',
        })
      );

      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || 'Сталася помилка при створенні рахунку';

      dispatch(
        addNotification({
          id: Math.random().toString(36).substr(2, 9),
          title: message,
          type: 'error',
        })
      );

      return rejectWithValue(message);
    }
  }
);

export const updateCashbox = createAsyncThunk(
  'cashboxes/updateCashbox',
  async ({ id, ...updateData }, { dispatch, rejectWithValue }) => {
    try {
      const token = TokenUtils.getToken();
      const response = await api.put(`/cashboxes/${id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      dispatch(
        addNotification({
          id: Math.random().toString(36).substr(2, 9),
          title: 'Рахунок оновлено',
          type: 'success',
        })
      );

      return response.data.cashbox;
    } catch (error) {
      const message = error.response?.data?.message || 'Не вдалося оновити рахунок';
      dispatch(
        addNotification({
          id: Math.random().toString(36).substr(2, 9),
          title: message,
          type: 'error',
        })
      );
      return rejectWithValue(message);
    }
  }
);

export const deleteCashbox = createAsyncThunk('cashboxes/deleteCashbox', async (id, { dispatch, rejectWithValue }) => {
  try {
    const token = TokenUtils.getToken();
    await api.delete(`/cashboxes/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    dispatch(
      addNotification({
        id: Math.random().toString(36).substr(2, 9),
        title: 'Рахунок видалено',
        type: 'success',
      })
    );

    return id;
  } catch (error) {
    const message = error.response?.data?.message || 'Не вдалося видалити рахунок';
    dispatch(
      addNotification({
        id: Math.random().toString(36).substr(2, 9),
        title: message,
        type: 'error',
      })
    );
    return rejectWithValue(message);
  }
});

export const transferMoney = createAsyncThunk(
  'cashboxes/transferMoney',
  async ({ from, to, amount }, { dispatch, rejectWithValue }) => {
    try {
      const token = TokenUtils.getToken();
      const response = await api.post(
        '/cashboxes/transfer',
        { from, to, amount },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      dispatch(
        addNotification({
          id: Math.random().toString(36).substr(2, 9),
          title: 'Переказ здійснено успішно',
          type: 'success',
        })
      );

      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || 'Сталася помилка під час переказу';

      dispatch(
        addNotification({
          id: Math.random().toString(36).substr(2, 9),
          title: message,
          type: 'error',
        })
      );

      return rejectWithValue(message);
    }
  }
);


export const updateCashboxPaymentMethods = createAsyncThunk(
  'cashboxes/updateCashboxPaymentMethods',
  async ({ id, ...updateData }, { dispatch, rejectWithValue }) => {
    try {
      const token = TokenUtils.getToken();
      const response = await api.put(`/cashboxes/payment-methods/${id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      dispatch(
        addNotification({
          id: Math.random().toString(36).substr(2, 9),
          title: 'Способи поповнення оновлено',
          type: 'success',
        })
      );

      return response.data.cashbox;
    } catch (error) {
      const message = error.response?.data?.message || 'Не вдалося оновити способи поповнення';
      dispatch(
        addNotification({
          id: Math.random().toString(36).substr(2, 9),
          title: message,
          type: 'error',
        })
      );
      return rejectWithValue(message);
    }
  }
);

const cashboxesSlice = createSlice({
  name: 'cashboxes',
  initialState: {
    cashboxes: [],
    error: null,
    isLoading: false,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCashboxes.fulfilled, (state, action) => {
        const incomingCashboxes = action.payload;

        incomingCashboxes.forEach((incoming) => {
          const index = state.cashboxes.findIndex((existing) => existing.id === incoming.id);

          if (index !== -1) {
            state.cashboxes[index] = incoming;
          } else {
            state.cashboxes.unshift(incoming);
          }
        });
      })
      .addCase(getCashboxes.rejected, (state, action) => {
        state.error = action.payload || 'Не вдалося отримати дані рахунків';
      })
      .addCase(getCashbox.fulfilled, (state, action) => {
        const existingIndex = state.cashboxes.findIndex((c) => c.id === action.payload.id);

        if (existingIndex !== -1) {
          state.cashboxes[existingIndex] = action.payload;
        } else {
          state.cashboxes.unshift(action.payload);
        }
      })
      .addCase(getCashbox.rejected, (state, action) => {
        state.error = action.payload || 'Не вдалося отримати дані рахунку';
      })
      .addCase(createCashbox.fulfilled, (state, action) => {
        const newCashbox = action.payload;
        const exists = state.cashboxes.find((c) => c.id === newCashbox.id);
        if (!exists) {
          state.cashboxes.unshift(newCashbox);
        }
      })
      .addCase(createCashbox.rejected, (state, action) => {
        state.error = action.payload || 'Не вдалося створити рахунок';
      })
      .addCase(updateCashbox.fulfilled, (state, action) => {
        const updated = action.payload;
        const index = state.cashboxes.findIndex((c) => c.id === updated.id);
        if (index !== -1) {
          state.cashboxes[index] = updated;
        }
      })
      .addCase(updateCashbox.rejected, (state, action) => {
        state.error = action.payload || 'Не вдалося оновити рахунок';
      })
      .addCase(deleteCashbox.fulfilled, (state, action) => {
        state.cashboxes = state.cashboxes.filter((c) => c.id !== action.payload);
      })
      .addCase(deleteCashbox.rejected, (state, action) => {
        state.error = action.payload || 'Не вдалося видалити рахунок';
      })
      .addCase(transferMoney.fulfilled, (state, action) => {
        const { fromBalance, toBalance } = action.payload;

        if (action.meta.arg.from && action.meta.arg.to) {
          const fromIndex = state.cashboxes.findIndex((c) => c.id === action.meta.arg.from);
          if (fromIndex !== -1) state.cashboxes[fromIndex].balance = fromBalance;

          const toIndex = state.cashboxes.findIndex((c) => c.id === action.meta.arg.to);
          if (toIndex !== -1) state.cashboxes[toIndex].balance = toBalance;
        }
      })
      .addCase(transferMoney.rejected, (state, action) => {
        state.error = action.payload || 'Не вдалося здійснити переказ';
      })
      .addCase(updateCashboxPaymentMethods.fulfilled, (state, action) => {
        const updated = action.payload;
        const index = state.cashboxes.findIndex((c) => c.id === updated.id);
        if (index !== -1) {
          state.cashboxes[index] = updated;
        }
      })
      .addCase(updateCashboxPaymentMethods.rejected, (state, action) => {
        state.error = action.payload || 'Не вдалося оновити способи поповнення';
      })
      .addMatcher(
        (action) => action.type.endsWith('/pending'),
        (state) => {
          state.isLoading = true;
          state.error = null;
        }
      )
      .addMatcher(
        (action) => action.type.endsWith('/fulfilled') || action.type.endsWith('/rejected'),
        (state) => {
          state.isLoading = false;
        }
      );
  },
});

export default cashboxesSlice.reducer;
